import React, { createContext, useCallback, useContext, useRef, useState } from "react";
import { createPortal } from "react-dom";

const ToastCtx = createContext(null);

export function ToastProvider({ children }) {
    const [toasts, setToasts] = useState([]); // { id, variant, title, description, duration }
    const timeoutsRef = useRef({}); // id -> timeout

    const remove = useCallback((id) => {
        setToasts((prev) => prev.filter((t) => t.id !== id));
        const t = timeoutsRef.current[id];
        if (t) {
            clearTimeout(t);
            delete timeoutsRef.current[id];
        }
    }, []);

    const toast = useCallback(({ variant = "info", title = "", description = "", duration = 3500 } = {}) => {
        const id = crypto.randomUUID ? crypto.randomUUID() : String(Date.now() + Math.random());
        setToasts((prev) => [...prev, { id, variant, title, description, duration }]);
        timeoutsRef.current[id] = setTimeout(() => remove(id), duration);
        return id;
    }, [remove]);

    const value = { toast, remove };

    return (
        <ToastCtx.Provider value={value}>
            {children}
            <Toaster toasts={toasts} onClose={remove} />
        </ToastCtx.Provider>
    );
}

export function useToast() {
    const ctx = useContext(ToastCtx);
    if (!ctx) throw new Error("useToast must be used within <ToastProvider>");
    return ctx;
}

function Toaster({ toasts, onClose }) {
    if (typeof document === "undefined") return null;
    return createPortal(
        <div
            aria-live="polite"
            aria-atomic="true"
            className="fixed bottom-4 right-4 z-[1000] flex flex-col gap-2 items-end
                 sm:bottom-6 sm:right-6"
        >
            {toasts.map((t) => (
                <ToastItem key={t.id} {...t} onClose={() => onClose(t.id)} />
            ))}
        </div>,
        document.body
    );
}

function variantStyles(variant) {
    switch (variant) {
        case "success":
            return "bg-emerald-600 text-white";
        case "error":
            return "bg-red-600 text-white";
        default:
            return "bg-slate-800 text-white";
    }
}

function Icon({ variant }) {
    return (
        <span className="shrink-0">
            {variant === "success" ? (
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M9 16.2 4.8 12l-1.4 1.4L9 19 21 7l-1.4-1.4z" /></svg>
            ) : variant === "error" ? (
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2 1 21h22L12 2zm1 15h-2v-2h2v2zm0-4h-2V8h2v5z" /></svg>
            ) : (
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M11 7h2v2h-2V7zm0 4h2v6h-2v-6zm1-9C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2z" /></svg>
            )}
        </span>
    );
}

function ToastItem({ id, variant, title, description, onClose }) {
    return (
        <div
            role="status"
            className={`w-80 rounded-lg shadow-lg px-3 py-2 flex gap-3 items-start ${variantStyles(variant)} 
                  transition transform will-change-transform`}
        >
            <Icon variant={variant} />
            <div className="flex-1">
                {title ? <p className="font-semibold leading-tight">{title}</p> : null}
                {description ? <p className="text-sm opacity-90">{description}</p> : null}
            </div>
            <button
                className="opacity-80 hover:opacity-100 focus:outline-none"
                aria-label="Cerrar notificación"
                onClick={onClose}
            >
                ×
            </button>
        </div>
    );
}
